import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BellRing,
  Clock,
  ExternalLink,
  Loader2,
  Mail,
  Pause,
  Play,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { alertAPI } from '../lib/api';
import {
  ALERT_ROW_TEST_ID_PREFIX,
  ALERTS_EMPTY_STATE_TEST_ID,
  ALERTS_ERROR_STATE_TEST_ID,
  ALERTS_PAGE_TEST_ID,
  alertListingHref,
  alertTitle,
  alertToggleParams,
  alertTypeLabel,
  applyAlertToggle,
  removeAlertById,
  sortAlertsNewestFirst,
} from '../lib/alertsDisplay';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function AlertsPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actingOn, setActingOn] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/alerts');
    }
  }, [authLoading, user, navigate]);

  const loadAlerts = async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await alertAPI.getAll();
      setAlerts(sortAlertsNewestFirst(res.data || []));
    } catch (error) {
      console.error('Failed to load alerts:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const toggleAlert = async (alert) => {
    setActingOn(alert.id);
    try {
      await alertAPI.update(alert.id, alertToggleParams(alert));
      setAlerts((prev) => applyAlertToggle(prev, alert.id));
    } catch (error) {
      console.error('Failed to update alert:', error);
      toast.error(t('alerts.update_failed', 'Could not update the alert. Please try again.'));
    } finally {
      setActingOn(null);
    }
  };

  const deleteAlert = async (alertId) => {
    setActingOn(alertId);
    try {
      await alertAPI.delete(alertId);
      setAlerts((prev) => removeAlertById(prev, alertId));
      toast.success(t('alerts.deleted', 'Alert deleted.'));
    } catch (error) {
      console.error('Failed to delete alert:', error);
      toast.error(t('alerts.delete_failed', 'Could not delete the alert. Please try again.'));
    } finally {
      setActingOn(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid={ALERTS_PAGE_TEST_ID}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-signal-blue/10 flex items-center justify-center">
              <BellRing className="w-6 h-6 text-signal-blue" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-800">
                {t('alerts.page_title', 'My Alerts')}
              </h1>
              <p className="text-sm text-slate-600">
                {t('alerts.page_subtitle', 'Property reminders and saved-search alerts you have set.')}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={loadAlerts}
            className="border-slate-300 text-slate-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('alerts.refresh', 'Refresh')}
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loadError ? (
            <div className="py-16 px-4 text-center" data-testid={ALERTS_ERROR_STATE_TEST_ID}>
              <BellRing className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-heading text-xl font-bold text-slate-800">
                {t('alerts.load_failed', 'Could not load your alerts.')}
              </h3>
              <Button
                variant="outline"
                onClick={loadAlerts}
                className="mt-4 border-slate-300 text-slate-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('alerts.retry', 'Try again')}
              </Button>
            </div>
          ) : alerts.length === 0 ? (
            <div className="py-16 px-4 text-center" data-testid={ALERTS_EMPTY_STATE_TEST_ID}>
              <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-heading text-xl font-bold text-slate-800">
                {t('alerts.empty_title', 'No alerts yet')}
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {t('alerts.empty_desc', 'Set a reminder from any property page and it will appear here.')}
              </p>
              <Button asChild className="mt-4 bg-signal-blue hover:bg-signal-blue-hover text-white">
                <Link to="/listings">{t('client.nav_browse_homes', 'Browse Homes')}</Link>
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {alerts.map((alert) => {
                const typeLabel = alertTypeLabel(alert);
                const title = alertTitle(alert);
                const href = alertListingHref(alert);
                const active = alert.is_active ?? true;

                return (
                  <div
                    key={alert.id}
                    className="p-4"
                    data-testid={`${ALERT_ROW_TEST_ID_PREFIX}${alert.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-2.5 h-2.5 rounded-full mt-2 ${active ? 'bg-signal-blue' : 'bg-slate-200'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800">
                            {title || t(typeLabel.labelKey, typeLabel.defaultLabel)}
                          </h3>
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-xs">
                            {t(typeLabel.labelKey, typeLabel.defaultLabel)}
                          </Badge>
                          {active ? (
                            <Badge className="bg-signal-blue/10 text-signal-blue border border-signal-blue/20 text-xs">
                              {t('alerts.active', 'Active')}
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-500 border border-slate-200 text-xs">
                              {t('alerts.paused', 'Paused')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                          {(alert.channels || []).includes('email') && (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {t('alerts.channel_email', 'Email')}
                            </span>
                          )}
                          {alert.created_at && (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {t('alerts.created_label', 'Created')}{' '}
                              {new Date(alert.created_at).toLocaleDateString()}
                            </span>
                          )}
                          {href && (
                            <Link
                              to={href}
                              className="inline-flex items-center gap-1 text-signal-blue hover:underline"
                            >
                              {t('alerts.view_property', 'View property')}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleAlert(alert)}
                          disabled={actingOn === alert.id}
                          className="border-slate-300 text-slate-700"
                        >
                          {actingOn === alert.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : active ? (
                            <>
                              <Pause className="w-4 h-4 mr-1" />
                              {t('alerts.pause', 'Pause')}
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-1" />
                              {t('alerts.resume', 'Resume')}
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteAlert(alert.id)}
                          disabled={actingOn === alert.id}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          aria-label={t('alerts.delete', 'Delete alert')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
